import { useState, useEffect } from 'react';
import { get } from '../lib/api';

interface OnboardingStatusResponse {
  id: string;
  tenant_id: string;
  status: string;
  current_step: string;
  completed_steps_json: string[];
  vertical_selected: string | null;
  integrations_configured_json: Record<string, unknown>;
  data_import_status: string | null;
  data_import_count: number;
  activated_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface UseOnboardingResult {
  operational_ready: boolean;
  percent_complete: number;
  current_step: string;
  loading: boolean;
  error: string | null;
}

const TOTAL_ONBOARDING_STEPS = 8;

export function useOnboarding(): UseOnboardingResult {
  const [result, setResult] = useState<UseOnboardingResult>({
    operational_ready: false, // wait for API response before allowing access
    percent_complete: 0,
    current_step: '',
    loading: true,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;

    get<OnboardingStatusResponse>('/api/onboarding/status')
      .then((data) => {
        if (cancelled) return;
        const completed = Array.isArray(data.completed_steps_json)
          ? data.completed_steps_json.length
          : 0;
        setResult({
          operational_ready: data.status === 'active',
          percent_complete: Math.min(
            100,
            Math.round((completed / TOTAL_ONBOARDING_STEPS) * 100),
          ),
          current_step: data.current_step ?? '',
          loading: false,
          error: null,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        // Fail-open: never block the operator on an API error
        setResult({
          operational_ready: true,
          percent_complete: 0,
          current_step: '',
          loading: false,
          error: err instanceof Error ? err.message : 'Unknown error',
        });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return result;
}

